// Split out from use-item-permissions.hook.ts: use-kit-permissions.hook.ts needs these,
// and use-item-permissions.hook.ts needs canAdminKit from utils/kit-permissions.ts —
// keeping the shared checks here avoids a circular import between the two hook files.
import { usePermissions } from '../use-permissions.hook';

/** Take custody for yourself: python-api `takeCustody` also accepts ITEMS_CUSTODY_OWN_TAKER. */
export const canTakeItemCustody = (permissions: ReturnType<typeof usePermissions>) =>
	permissions.useCustody &&
	permissions.hasAnyPermission('ITEMS_CUSTODY_TAKER', 'ITEMS_CUSTODY_TAKER_RESTRICTED', 'ITEMS_CUSTODY_OWN_TAKER');

/** Take custody on behalf of somebody else: the own taker never covers that. */
const canTakeItemCustodyForOthers = (permissions: ReturnType<typeof usePermissions>) =>
	permissions.useCustody && permissions.hasAnyPermission('ITEMS_CUSTODY_TAKER', 'ITEMS_CUSTODY_TAKER_RESTRICTED');

/** Transfer to anybody: the any-tier transferer. */
const canTransferItemCustodyToAnybody = (permissions: ReturnType<typeof usePermissions>) =>
	permissions.useCustody &&
	permissions.hasAnyPermission('ITEMS_CUSTODY_TRANSFERER', 'ITEMS_CUSTODY_TRANSFERER_RESTRICTED');

/**
 * Transfer custody; `own`: the item is already in the signed-in user's custody. python-api
 * `transferCustody`: ITEMS_CUSTODY_OWN_TRANSFERER only moves somebody else's item to yourself, so it
 * counts only when `own` is known to be `false`.
 */
export const canTransferItemCustody = (permissions: ReturnType<typeof usePermissions>, own?: boolean) =>
	canTransferItemCustodyToAnybody(permissions) ||
	(!!permissions.useCustody && own === false && permissions.hasAnyPermission('ITEMS_CUSTODY_OWN_TRANSFERER'));

export const canReleaseItemCustody = (permissions: ReturnType<typeof usePermissions>, own?: boolean) =>
	permissions.useCustody &&
	(permissions.hasAnyPermission('ITEMS_CUSTODY_RELEASER', 'ITEMS_CUSTODY_RELEASER_RESTRICTED') ||
		(own && permissions.hasAnyPermission('ITEMS_CUSTODY_OWN_RELEASER')));

export const canGiveItemCustody = (permissions: ReturnType<typeof usePermissions>) =>
	permissions.hasAnyPermission('CUSTOMERS_READER') &&
	// Giving custody picks somebody else: the own transferer never covers that.
	canTransferItemCustodyToAnybody(permissions) &&
	canTakeItemCustodyForOthers(permissions);

export const canAdminItem = (permissions: ReturnType<typeof usePermissions>) =>
	permissions.hasAnyPermission('ITEMS_ADMIN', 'ITEMS_ADMIN_RESTRICTED');

export const canUpdateItemGeo = (permissions: ReturnType<typeof usePermissions>) =>
	permissions.useGeo && permissions.hasAnyPermission('ITEMS_GEO_ADMIN', 'ITEMS_GEO_ADMIN_RESTRICTED');
